import React from 'react';
import {FormControl, FormLabel, InputAdornment, SxProps, TextField, TextFieldProps, Typography} from '@mui/material';
import {useFormContext} from 'react-hook-form';
import {Theme} from '@mui/material/styles';
import {Lock} from '@mui/icons-material';

export type FormTextInputProps = TextFieldProps & {
    name: string;
    label: string;
    locked?: boolean;
    aide?: string;
    hidden?: boolean;
    /** Disable password manager icons (LastPass, 1Password, etc.) */
    disablePasswordManager?: boolean;
    /** End adornment to display in the input */
    endAdornment?: React.ReactNode;
    /** Test ID for E2E testing */
    testId?: string;
    /** Maximum character length (HTML attribute) */
    maxLength?: number;
};

/** Thresholds for progressive character counter visibility */
const SHOW_THRESHOLD = 0.8; // Always show counter when > 80% full
const WARNING_THRESHOLD = 0.9; // Warning color when > 90% full
const MIN_LENGTH_FOR_COUNTER = 100; // Only show counter for "free text" fields (not phone, postal code, etc.)

/**
 * Get the color for the character counter based on current length vs max
 */
function getCounterColor(current: number, max: number): 'text.secondary' | 'warning.main' | 'error.main' {
    if (current > max) return 'error.main';
    if (current / max >= WARNING_THRESHOLD) return 'warning.main';
    return 'text.secondary';
}

function FormTextInput({
                           name,
                           label,
                           size = 'small',
                           fullWidth = true,
                           locked = false,
                           disabled,
                           aide,
                           hidden,
                           sx,
                           required,
                           slotProps,
                           disablePasswordManager = false,
                           endAdornment,
                           testId,
                           maxLength,
                           ...rest
                       }: FormTextInputProps) {
    const {register, formState, watch} = useFormContext();
    const [isFocused, setIsFocused] = React.useState(false);

    // Watch the field value for character counting
    const fieldValue = watch(name) as string | undefined;
    const currentLength = fieldValue?.length ?? 0;

    if (hidden) return null;
    const error = formState.errors[name];
    const errorMessage = error?.message && typeof error.message === 'string' ? error.message : undefined;

    // Progressive visibility logic for character counter
    // Only for "free text" fields (maxLength > 100), not for phone, postal code, etc.
    const isCounterEnabled = maxLength && maxLength >= MIN_LENGTH_FOR_COUNTER;
    const shouldShowCounter = isCounterEnabled && (
        isFocused || // Show on focus
        currentLength / maxLength >= SHOW_THRESHOLD || // Show when approaching limit
        currentLength > maxLength // Always show when exceeded
    );
    const counterColor = maxLength ? getCounterColor(currentLength, maxLength) : 'text.secondary';

    const handleFocus = () => setIsFocused(true);
    const handleBlur = () => setIsFocused(false);


    const labelId = `${name}_label`;

    const styles: SxProps<Theme> = {...sx, backgroundColor: locked ? 'grey.200' : ''};

    // Get register props, excluding name if disablePasswordManager is true
    const getRegisterProps = () => {
        const { name: _name, ...rest } = register(name);
        return disablePasswordManager ? rest : register(name);
    };

    // Props to disable password managers (LastPass, 1Password, etc.)
    const passwordManagerProps = disablePasswordManager ? {
        'data-lpignore': true,
        autoComplete: 'off',
    } : {};

    // Build character counter element
    const renderCounter = () => {
        if (!shouldShowCounter || !maxLength) return null;
        return (
            <Typography
                component="span"
                variant="caption"
                sx={{ color: counterColor, whiteSpace: 'nowrap', ml: 0.5 }}
            >
                {currentLength}/{maxLength}
            </Typography>
        );
    };

    // Build input slot props
    const getInputSlotProps = () => {
        const counter = renderCounter();

        if (locked) {
            return {
                endAdornment: (
                    <InputAdornment position="end">
                        <Lock sx={{color: 'grey.400'}}/>
                    </InputAdornment>
                ),
            };
        }

        // Combine existing endAdornment with counter
        const combinedAdornment = (endAdornment || counter) ? (
            <InputAdornment position="end">
                {endAdornment}
                {counter}
            </InputAdornment>
        ) : null;

        return {
            ...(combinedAdornment && { endAdornment: combinedAdornment }),
            autoComplete: 'off',
        };
    };

    // Build htmlInput slot props (includes required attribute for accessibility)
    const getHtmlInputSlotProps = () => ({
        ...slotProps?.htmlInput,
        ...passwordManagerProps,
        ...(testId && { 'data-testid': testId }),
        ...(required && { required: true }),
        ...(maxLength && { maxLength }),
        'aria-labelledby': labelId,
    });

    // Helper text (error message or aide text)
    const helperText = errorMessage ?? aide;

    return (
        <FormControl error={!!error} fullWidth={fullWidth} size={size}>
            <FormLabel id={labelId} required={required}>{label}</FormLabel>
            <TextField
                {...rest}
                {...getRegisterProps()}
                onFocus={handleFocus}
                onBlur={handleBlur}
                slotProps={{
                    input: getInputSlotProps(),
                    htmlInput: getHtmlInputSlotProps(),
                    inputLabel: {shrink: true},
                }}
                error={!!error}
                aria-labelledby={labelId}
                size={size}
                fullWidth={fullWidth}
                helperText={helperText}
                disabled={disabled ?? locked}
                sx={styles}
            />
        </FormControl>
    );
}

export default FormTextInput;
